import { type DefaultRootState } from 'react-redux';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { type ActiveUser } from '@bbkAdminRedux/app/reducers';
import {
  authStatusFromQuery,
  hasUsableAuthenticityToken,
  selectHeldAuthToken,
} from '@bbkAdminRedux/rtkq/auth-status';

export enum BBKRequestContext {
  reports_filter = 'reports_filter',
  deflection_funnel = 'deflection_funnel',
  benchmark_card_prior = 'benchmark_card_prior',
  benchmark_card_current = 'benchmark_card_current',
  exp_perf = 'exp_perf',
  offer_perf = 'offer_perf',
  lifecycle_reports = 'lifecycle_reports',
}

export type BBKRequestContextArgs = {
  context?: BBKRequestContext;
};

const bbkFetchBaseQuery: typeof fetchBaseQuery = (args) => {
  let fbqArgs = {};
  if (process.env.NODE_ENV === 'test') {
    fbqArgs = {
      baseUrl: 'http://localhost:3000',
    };
  }
  const rawBaseQuery = fetchBaseQuery({
    ...fbqArgs,
    ...args,
    prepareHeaders: (headers, api) => {
      if (api.endpoint === bbkApiSlice.endpoints.getCurrentUser.name) {
        return headers;
      }
      const state = api.getState() as DefaultRootState;

      // Prefer the in-memory token held during sign-in (before the global
      // getCurrentUser query is populated), then fall back to the query cache
      // token. Both paths use the shared usable-token definition.
      const heldToken = selectHeldAuthToken(state);
      if (heldToken) {
        headers.set('BBK-Authenticity-Token', heldToken);
      } else {
        const { data: currentUser } =
          bbkApiSlice.endpoints.getCurrentUser.select()(state);
        if (hasUsableAuthenticityToken(currentUser)) {
          headers.set('BBK-Authenticity-Token', currentUser.authenticity_token);
        }
      }

      return headers;
    },
  });

  // Short-circuit requests made without a session (signed out, or transmitted
  // while parked on the sign-in / selector page during a redirect race) instead
  // of hitting the network.
  //
  // A few endpoints are global (not company/app-scoped) and must still run
  // while signed out: getCurrentUser is the auth probe (its 401 is how a
  // signed-out user is detected), and getGlobalFFs powers the signed-out
  // sign-in page (e.g. which auth method to offer — without it the dev-login
  // path is hidden and sign-in breaks).
  const guardedBaseQuery: typeof rawBaseQuery = (bqArgs, api, extraOptions) => {
    // Resolved lazily at request time — bbkApiSlice is not yet initialized
    // when bbkFetchBaseQuery() runs at module load.
    const signedOutAllowedEndpoints = [
      bbkApiSlice.endpoints.getCurrentUser.name,
      'getGlobalFFs',
    ];
    const isAllowedWhileSignedOut = signedOutAllowedEndpoints.includes(
      api.endpoint
    );
    // Signed-in state is derived from the /api/current_user probe (the server's
    // authority on the session) rather than reading a JS-visible JWT cookie. The
    // shared mapper is 401-aware, so a mid-session 401 that leaves stale user
    // data cached no longer counts as authenticated.
    const authStatus = authStatusFromQuery(
      bbkApiSlice.endpoints.getCurrentUser.select()(
        api.getState() as DefaultRootState
      )
    );
    if (
      process.env.NODE_ENV !== 'test' &&
      !isAllowedWhileSignedOut &&
      authStatus !== 'authenticated'
    ) {
      return {
        error: {
          status: 'CUSTOM_ERROR',
          error: 'Request skipped: user is not signed in',
        },
      };
    }

    return rawBaseQuery(bqArgs, api, extraOptions);
  };

  return guardedBaseQuery;
};
const bbkPreauthFetchBaseQuery: typeof fetchBaseQuery = (args) => {
  let fbqArgs = {};
  if (process.env.NODE_ENV === 'test') {
    fbqArgs = {
      baseUrl: 'http://localhost:3000',
    };
  }
  return fetchBaseQuery({
    ...fbqArgs,
    ...args,
    prepareHeaders: (headers, api) => {
      const state = api.getState() as DefaultRootState;

      // Prefer the in-memory token held during sign-in (so the company-creation
      // POSTs authenticate before the global getCurrentUser query is
      // populated), then fall back to the query cache token.
      const heldToken = selectHeldAuthToken(state);
      if (heldToken) {
        headers.set('BBK-Authenticity-Token', heldToken);
      } else {
        const { data: currentUser } =
          bbkApiSlice.endpoints.getCurrentUser.select()(state);
        if (hasUsableAuthenticityToken(currentUser)) {
          headers.set('BBK-Authenticity-Token', currentUser.authenticity_token);
        }
      }

      return headers;
    },
  });
};
export const BBK_FETCH_BASE_QUERY = bbkFetchBaseQuery();
export const BBK_PREAUTH_FETCH_BASE_QUERY = bbkPreauthFetchBaseQuery();
export const bbkApiSlice = createApi({
  reducerPath: 'bbkApi-rktq',
  baseQuery: BBK_FETCH_BASE_QUERY,
  tagTypes: [
    'Session',
    'Notification',
    'Funnels',
    'FunnelRoutingMetadata',
    'FunnelListingMetadata',
    'LiveFunnels',
    'PageAndOfferCounts',
    'Experience',
    'Placements',
    'Offers',
    'Routing',
    'CurrentUser',
    'CurrentCompany',
    'CurrentApp',
    'UserCompanyRoles',
    'LifecycleState',
    'OnboardingHistory',
    'OnboardingStepsHistory',
    'Branding',
    'Integrations',
    'FieldMappings',
    'IntegrationFields',
    'BillingConfigs',
    'PricingPageConfig',
    'RefundSettings',
    'CspDomains',
    'PricingPage',
    'PricingTable',
    'CompanySettings',
    'AppSettings',
    'GlobalUrls',
    'AlertsAndWebhooks',
    'AlertNotification',
    'NotificationConfigs',
    'ActivityLog',
    'ApiKeys',
    'Teams',
    'VanityDomain',
    'DefaultVanityDomain',
    'CompanySubscriptions',
    'CompanySubscriptionsHistory',
    'Plans',
    'Invoices',
    'Coupons',
    'Companies',
    'CompanyUsers',
    'ConfirmationFunnels',
    'PendingInvitesByCompany',
    'PendingInvitesByEmail',
    'OfferConfigs',
    'LossAversions',
    'Rules',
    'Reasons',
    'ReasonTranslations',
    'AdditionalBrands',
    'IntegrationFieldValues',
    'Plays',
    'PlaysMetaData',
    'CancelPlayConfig',
    'Products',
    'Optimizations',
    'BrandingVariants',
    'ApplicationLocales',
    'LanguageSourceConfig',
    'OfferLocaleStates',
    'LossAversionLocaleStates',
    'ConfirmationPageLocaleStates',
    'CancelPageLocaleStates',
  ],
  endpoints: (build) => ({
    getCurrentUser: build.query<ActiveUser, void>({
      query: () => ({
        url: '/api/current_user',
      }),
      providesTags: ['CurrentUser', 'CurrentCompany', 'CurrentApp'],
    }),
  }),
});
